import { RootState, useAppSelector } from '@Store/store';
import React, { useEffect, useRef, useState } from 'react'
import { DateRange, Range } from 'react-date-range';
import { format, subDays, addDays, startOfMonth, endOfMonth, startOfYear, endOfYear, subMonths, subYears } from 'date-fns';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  LineChart,
  Line,
} from 'recharts';
import { useLazyGetDashboardMetricsQuery } from './apiSlice';
import Loader from '@Components/Loader';
import { AnalyticsParamBuilder, formatNumber, useUpgradeContext } from '@Utils/common';
import { displayToast } from '@Utils/toast';
import posthog from 'posthog-js';



interface Stat {
  title: string;
  value: string | number;
}



interface StatCardsProps {
  stats: Stat[];
}

interface OverviewData {
  date: string;
  sent: Number;
  seen: Number;
}

interface OverviewDataProp {
  data: OverviewData[]
}


interface FollowersGainedData {
  date: string;
  followers: Number;
}

interface FollowersGainedProp {
  data: FollowersGainedData[]
}

interface DateRangeDropdownProps {
  range: Range[];
  setRange: React.Dispatch<React.SetStateAction<Range[]>>;
}


const FollowersGainedChart: React.FC<FollowersGainedProp> = ({ data }) => {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className='sm:mb-4'>
        <h2 className="text-xl font-semibold">Followers Gained
          <span className="text-xs font-light ml-2 hidden sm:inline">(using Ask for Follow feature)</span></h2>
      </div>
      <span className="text-xs font-light mb-4 inline sm:hidden">(using Ask for Follow feature)</span>

      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="date" />
          <YAxis allowDecimals={false} width={30} tickFormatter={(value) => {
            if (value >= 1000) {
              return `${(value / 1000).toFixed(0)}k`; // e.g. 1500 → 1k
            }
            return value;
          }} />
          <Tooltip />
          <Legend />
          <Bar dataKey="followers" fill="#3b82f6" barSize={24} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const OverviewChart: React.FC<OverviewDataProp> = ({ data }) => {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h2 className="text-xl font-semibold mb-4">Overview</h2>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="date" />
          <YAxis allowDecimals={false} width={30} tickFormatter={(value) => {
            if (value >= 1000) {
              return `${(value / 1000).toFixed(0)}k`; // e.g. 1500 → 1k
            }
            return value;
          }} />
          {/* <YAxis tickFormatter={(val) => `${val / 1000}k`} /> */}
          <Tooltip />
          <Legend />

          <Line
            type="monotone"
            dataKey="sent"
            stroke="#ffc658"
            strokeWidth={2}
            dot={{ r: 2 }}
          />
          <Line
            type="monotone"
            dataKey="seen"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={{ r: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const StatCards: React.FC<StatCardsProps> = ({ stats }) => {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {stats.map((stat: any, index: number) => (
        <div
          key={index}
          className="text-center bg-white rounded-lg border border-gray-200 p-4 shadow-sm flex flex-col justify-between"
        >
          <span className="text-2xl sm:text-4xl font-semibold text-gray-900">{stat.value}</span>
          <span className="text-sm sm:text-md text-gray-500">{stat.title}</span>
        </div>
      ))}
    </div>
  );
};

const COMPONENT = "dashboard"

const DashboardMetrics = () => {

  const { user } = useAppSelector((state: RootState) => state.profile);
  const { selectedIgId, selectedIgData } = useAppSelector((state: RootState) => state.igSelection);

  const [CardData, setCardData] = useState<Stat[]>([])

  const [OverviewData, setOverviewData] = useState<OverviewData[]>([])

  const [range, setRange] = useState<Range[]>([
    {
      startDate: format(subDays(new Date(), 6), 'yyyy-MM-dd'),
      endDate: format(new Date(), 'yyyy-MM-dd'),
      key: 'selection',
    },
  ]);

  const [FollowersGained, setFollowersGained] = useState<FollowersGainedData[]>([])

  const [getMetrics, { data: dashboardMetricsData, isLoading: dashboardMetricsIsLoading, error: dashboardMetricsError, isFetching, isUninitialized, isSuccess, isError }] = useLazyGetDashboardMetricsQuery();

  const { setUpgradeToPro } = useUpgradeContext();

  const [LimitReached, setLimitReached] = useState(false)





  // Use selected IG account if available, otherwise fallback to first account
  const ig_user = selectedIgData || user?.ig_ids?.[0]


  useEffect(() => {
    const payment_status = user?.status
    if (ig_user) {
      const automation_triggered = ig_user?.automation_triggered
      const ig_contacts = ig_user?.contacts

      if (!user?.is_pro && (automation_triggered > 1000 || ig_contacts > 1000)) {
        setLimitReached(true)
      }
    }
  }, [user])

  useEffect(() => {
    if (ig_user?.igsid) {
      getMetrics({
        from_date: range?.[0]?.startDate,
        to_date: range?.[0]?.endDate,
        ig_id: ig_user.igsid,
      })
    }
  }, [ig_user])

  useEffect(() => {
    if (ig_user?.igsid) {
      getMetrics({
        from_date: range?.[0]?.startDate,
        to_date: range?.[0]?.endDate,
        ig_id: ig_user.igsid,
      })
    }
  }, [range, ig_user])

  useEffect(() => {
    if (dashboardMetricsData) {
      const metric_data = dashboardMetricsData?.data
      const temp_card_data_obj: any = {
        'sent': 0,
        'seen': 0,
        'total_clicks': 0,
        'followers_gained': 0,
        'ctr': 0
      }

      const temp_overview_data = []
      const temp_followers_gained_data = []
      for (let index = 0; index < metric_data.length; index++) {
        const row = metric_data[index];

        const dateStr = new Date(row?.server_date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        });
        temp_overview_data.push({
          date: dateStr,
          seen: row?.opened,
          sent: row?.sent
        })
        temp_followers_gained_data.push({
          date: dateStr,
          followers: row?.followers
        })
        temp_card_data_obj['sent'] += row?.sent
        temp_card_data_obj['seen'] += row?.opened
        temp_card_data_obj['total_clicks'] += row?.clicked
        temp_card_data_obj['followers_gained'] += row?.followers
      }
      temp_card_data_obj['ctr'] = (temp_card_data_obj['clicked'] / temp_card_data_obj['sent']).toFixed(2)


      const temp_card_data: Stat[] = []

      const data_titles = ['Sent', 'Seen', 'Total Clicks', 'Followers Gained']
      for (let index = 0; index < data_titles.length; index++) {
        const title = data_titles[index];
        const title_converted = title.toLowerCase().replace(' ', "_")
        temp_card_data.push({
          title: title,
          value: temp_card_data_obj[title_converted].toLocaleString()
        })
      }

      setCardData(temp_card_data)
      setOverviewData(temp_overview_data)
      setFollowersGained(temp_followers_gained_data)
    }

  }, [dashboardMetricsData, dashboardMetricsIsLoading, dashboardMetricsError])

  const OnClickUpgrade = (from: string) => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "on_upgrade_click",
      classValue: from,
    }));
    if (user?.status == "pending") {
      displayToast("error", "Your Subscription payment is Pending! Contact Support ASAP");
      return
    }
    setUpgradeToPro(true)
  }




  return (
    <div className='p-4'>
      {/* <div className="border-b w-full pb-3 rounded-lg border-gray-200">
        <h1 className="text-xl md:text-xl font-medium">Dashboard</h1>
      </div> */}
      {false && !user?.is_pro && <div className="bg-[#2e006c] text-white p-4 rounded-lg flex flex-col sm:flex-row items-center sm:justify-between gap-4 w-full w-full mx-auto">

        {/* Left Section */}
        <div className="flex items-center text-center sm:text-left">
          {/* <div className="text-3xl text-orange-400">
            <ClockIcon className='size-10' />
          </div> */}
          <div>
            <h2 className="font-bold text-2xl">
              <span className='text-[#ffd86e]'>🪔 Diwali Offer 🪔</span><br className='sm:hidden' /> Flat 60% off on Pro!
            </h2>
            <p className="text-md text-white">
              Limited Time Only
            </p>
          </div>
        </div>

        {/* Right Section */}
        <button
          onClick={() => OnClickUpgrade('early_bird')}
          className="bg-[#ffd86e] hover:opacity-[.9] text-black text-center font-semibold px-2 py-2 rounded-full text-md w-full sm:w-[200px]"
        >
          Upgrade Now
        </button>
      </div>}
      {false && !user?.is_pro && LimitReached && (
        <div onClick={() => OnClickUpgrade('limit_reached')} className="hover:cursor-pointer my-3 rounded-lg shadow-md bg-gradient-to-l text from-red-500 to-red-700 p-4 text-white">
          <p className="text-lg sm:text-lg font-semibold mb-1">
            Limit Reached – Unlock Pro Today!
          </p>
          <p className="text-sm sm:text-base opacity-90 ">
            Your automations will pause soon. Upgrade now to keep them running.
          </p>
        </div>
      )}


      <div className='space-y-4 mt-2'>
        <h2 className="text-[1.8rem] sm:text-[2.6rem] font-black">Hello, <span className='bg-gradient-to-b from-[#7878ff] to-[#3535d5] bg-clip-text text-transparent p-[2px]'>{user?.first_name}!</span> 👋</h2>
        <div>
          {/* Red border box */}
          <div className="rounded-md inline-block">
            <div className="flex items-center space-x-4">
              {/* Profile Image */}
              <img
                src={ig_user?.profile_picture_url}
                alt="Profile"
                className="w-20 h-20 rounded-full object-cover"
              />

              {/* Profile Info */}
              <div>
                <p className="text-sm mb-2 sm:text-xl font-semibold">@{ig_user?.username}</p>
                <p className="w-full text-sm text-gray-800 flex space-x-8">
                  <span className="flex flex-col items-center">
                    <span className="font-medium">{formatNumber(ig_user?.media_count)}</span>
                    <span className="text-xs">posts</span>
                  </span>
                  <span className="flex flex-col items-center">
                    <span className="font-medium">{formatNumber(ig_user?.followers_count)}</span>
                    <span className="text-xs">followers</span>
                  </span>
                  <span className="flex flex-col items-center">
                    <span className="font-medium">{formatNumber(ig_user?.follows_count)}</span>
                    <span className="text-xs">following</span>
                  </span>
                </p>

                {/* <p className="text-sm text-gray-700 italic">
                                    Clairvoyant, Pernicious & Clandestine
                                </p> */}
              </div>
            </div>
          </div>
        </div>
      </div>
      {dashboardMetricsIsLoading || isFetching ? <Loader isLoading={true} /> :
        <div className='space-y-3 mt-5'>
          <div className="flex items-center justify-between mt-3">
            <h3 className="text-xl sm:text-2xl font-semibold">Analytics</h3>
            <DateRangeDropdown range={range} setRange={setRange} />
          </div>

          <div>
            <StatCards stats={CardData} />
          </div>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4'>
            <OverviewChart data={OverviewData} />
            <FollowersGainedChart data={FollowersGained} />
          </div>
        </div>}

    </div>
  )
}



const DateRangeDropdown: React.FC<DateRangeDropdownProps> = ({ range, setRange }) => {
  const [isCustomRangeSelected, setIsCustomRangeSelected] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string>('Last 7 days');

  // local temp range (kept in sync with prop)
  const [tempRange, setTempRange] = useState<Range[]>(range);

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const startStr = range[0]?.startDate
    ? typeof range[0].startDate === "string"
      ? range[0].startDate
      : format(range[0].startDate as Date, "yyyy-MM-dd")
    : "";

  const endStr = range[0]?.endDate
    ? typeof range[0].endDate === "string"
      ? range[0].endDate
      : format(range[0].endDate as Date, "yyyy-MM-dd")
    : "";


  // keep tempRange in sync whenever parent range changes
  useEffect(() => {
    setTempRange(range);

    // derive the label from the incoming range (strings in your case)
    const startStr = (range?.[0]?.startDate as any) ?? '';
    const endStr = (range?.[0]?.endDate as any) ?? '';
    const today = format(new Date(), 'yyyy-MM-dd');

    if (startStr === today && endStr === today) setSelectedLabel('Today');
    else if (startStr === format(subDays(new Date(), 1), 'yyyy-MM-dd') && endStr === format(subDays(new Date(), 1), 'yyyy-MM-dd'))
      setSelectedLabel('Yesterday');
    else if (startStr === format(subDays(new Date(), 6), 'yyyy-MM-dd') && endStr === today)
      setSelectedLabel('Last 7 days');
    else if (startStr === format(subDays(new Date(), 29), 'yyyy-MM-dd') && endStr === today)
      setSelectedLabel('Last 30 days');
    else if (
      startStr === format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd') &&
      endStr === format(endOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd')
    )
      setSelectedLabel('Last month');
    else if (
      startStr === format(startOfYear(subYears(new Date(), 1)), 'yyyy-MM-dd') &&
      endStr === format(endOfYear(subYears(new Date(), 1)), 'yyyy-MM-dd')
    )
      setSelectedLabel('Last year');
    else setSelectedLabel('Custom Range');
  }, [range]);

  const formatRangeLabel = (r: Range) => {
    if (!r || !r.startDate || !r.endDate) return '';
    return `${r.startDate} - ${r.endDate}`;
  };

  const handlePreset = (label: string) => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "selected_date",
      str_value: label,
    }));
    const today = new Date();
    let startDate = today;
    let endDate = today;

    switch (label) {
      case 'Today':
        startDate = endDate = today;
        break;
      case 'Yesterday':
        startDate = endDate = subDays(today, 1);
        break;
      case 'Last 7 days':
        startDate = subDays(today, 6);
        break;
      case 'Last 30 days':
        startDate = subDays(today, 29);
        break;
      case 'Last month': {
        const prevMonth = subMonths(today, 1);
        startDate = startOfMonth(prevMonth);
        endDate = endOfMonth(prevMonth);
        break;
      }
      case 'Last year': {
        const prevYear = subYears(today, 1);
        startDate = startOfYear(prevYear);
        endDate = endOfYear(prevYear);
        break;
      }
      default:
        break;
    }

    // keep strings (your backend format)
    const newRange: Range[] = [
      {
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        key: 'selection',
      },
    ];
    setRange(newRange);
    setTempRange(newRange);
    setSelectedLabel(label);
    setShowPicker(false);
  };

  const OnDatePickerDropdownClick = () => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "select_date_dropdown_click",
    }));
    setShowPicker(!showPicker)
  }

  return (
    <div ref={dropdownRef} className="relative inline-block text-sm">
      <button
        onClick={OnDatePickerDropdownClick}
        className="flex items-center justify-center gap-2 px-3 py-2 sm:px-4 sm:py-2 
             bg-white border border-gray-300 rounded-md shadow-sm
             hover:bg-gray-50 active:shadow-inner transition-all duration-150
             text-xs sm:text-sm font-medium text-gray-700"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>

        {/* Show label or formatted range */}
        {selectedLabel === 'Custom Range' || selectedLabel === 'Select Date'
          ? (
            <span className="truncate max-w-[100px] sm:max-w-none text-center">
              {formatRangeLabel(range[0])}
            </span>
          )
          : (
            <span>{selectedLabel}</span>
          )
        }
      </button>


      {showPicker && (
        <div className="absolute bg-white right-0 sm:left-auto sm:right-0 z-50 mt-2 flex flex-col sm:flex-row w-full bg-white rounded shadow-lg border">
          <div className="w-full border-b sm:border-b-0 sm:border-r p-2 flex flex-col gap-1 text-gray-700">
            {['Last 7 days', 'Last 30 days', 'Last month'].map((label) => (
              <button
                key={label}
                onClick={() => handlePreset(label)}
                className="text-left px-2 py-1 rounded hover:bg-indigo-50 focus:bg-indigo-100 focus:outline-none"
              >
                {label}
              </button>
            ))}
          </div>

          <div className="absolute right-[0%] top-[100%] p-2 bg-white">
            <DateRange
              editableDateInputs={true}
              onChange={(item) => {
                // keep tempRange as strings for your backend
                setTempRange([
                  {
                    startDate: item.selection.startDate ? format(item.selection.startDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
                    endDate: item.selection.endDate ? format(item.selection.endDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
                    key: 'selection',
                  },
                ]);
                setIsCustomRangeSelected(true);
              }}
              moveRangeOnFirstSelection={false}
              ranges={[
                {
                  ...tempRange[0],
                  startDate: tempRange[0].startDate ? new Date(tempRange[0].startDate as any) : undefined,
                  endDate: tempRange[0].endDate ? new Date(tempRange[0].endDate as any) : undefined,
                },
              ]}
              rangeColors={['#6366f1']}
            />

            <div className="flex justify-end gap-2 mt-2 bg-white">
              <button
                onClick={() => {
                  const todayStr = format(new Date(), 'yyyy-MM-dd');
                  const cleared = [{ startDate: todayStr, endDate: todayStr, key: 'selection' }];
                  setTempRange(cleared);
                }}
                className="px-3 py-1 rounded border text-gray-600 hover:bg-gray-100"
              >
                Clear
              </button>
              <button
                onClick={() => {
                  if (isCustomRangeSelected) setSelectedLabel('Custom Range');
                  setRange(tempRange);
                  setShowPicker(false);
                  setIsCustomRangeSelected(false);
                }}
                className="px-4 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



export default DashboardMetrics